// Security settings — placeholder M3 (admin + sellers + vendors).
//
// Volkan direction 2026-05-23 (Settings 10-section expansion item #9):
// 2FA / session list / API token placeholder cards. Backend persistence M3
// Stage 4 SPEC.md Faz 1 (iam-auth foundational + audit_events) ratify.

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Key, Monitor, ShieldCheck } from 'lucide-react'
import { Button } from '../../components/ui/button'
import { Badge } from '../../components/ui/badge'
import { useT } from '../../i18n'
import { formatDateTime } from '../../lib/datetime'
import { securityI18n } from '../../i18n/settings/security'
import { apiUrl, USE_REAL_API } from '../../lib/api'
import { useToastStore } from '../../lib/toast'
import { useAuthStore } from '../../store/auth'
import type { SettingsSurface } from './Settings'

// Session metadata preserved as proper-noun-style data (Chrome, macOS, city) —
// locale tercihi sadece chrome'u etkiler, session subject EN preserve eder
// (data-driven).
type SessionMeta = {
  id: string
  signature: string
  isCurrent: boolean
}

const SESSIONS: SessionMeta[] = [
  { id: 'chrome-mac', signature: 'Chrome · macOS · Austin, TX', isCurrent: true },
  { id: 'safari-ios', signature: 'Safari · iOS · Austin, TX', isCurrent: false },
]

type ApiSession = {
  id: string
  user_agent: string | null
  ip_address: string | null
  issued_at: string
  refreshed_at: string | null
  expires_at: string
}

function describeUserAgent(
  ua: string | null,
  labels: { unknownDevice: string; unknownOs: string; browser: string },
): string {
  if (!ua) return labels.unknownDevice
  const browser =
    /Edg\//.test(ua) ? 'Edge'
    : /Chrome\//.test(ua) ? 'Chrome'
    : /Firefox\//.test(ua) ? 'Firefox'
    : /Safari\//.test(ua) ? 'Safari'
    : labels.browser
  const os =
    /Windows/.test(ua) ? 'Windows'
    : /Mac OS X|Macintosh/.test(ua) ? 'macOS'
    : /iPhone|iPad|iOS/.test(ua) ? 'iOS'
    : /Android/.test(ua) ? 'Android'
    : /Linux/.test(ua) ? 'Linux'
    : labels.unknownOs
  return `${browser} · ${os}`
}

// Real-engine Security: live change-password + active sessions over the backend.
// 2FA + API tokens stay honest V1.5 placeholders (no backend yet).
function RealSecurity() {
  const push = useToastStore((s) => s.push)
  const setTokens = useAuthStore((s) => s.setTokens)
  const qc = useQueryClient()
  const t = useT(securityI18n).real
  const [current, setCurrent] = useState('')
  const [next, setNext] = useState('')
  const [confirm, setConfirm] = useState('')

  const sessions = useQuery<{ items: ApiSession[]; total: number }>({
    queryKey: ['auth-sessions'],
    queryFn: async () => {
      const res = await fetch(apiUrl('/auth/sessions'))
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
  })

  const changePassword = useMutation({
    mutationFn: async () => {
      const res = await fetch(apiUrl('/auth/change-password'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ current_password: current, new_password: next }),
      })
      if (!res.ok) {
        const body = await res.json().catch(() => null)
        throw new Error(body?.detail?.code ?? `HTTP ${res.status}`)
      }
      return res.json() as Promise<{ access_token: string; refresh_token: string }>
    },
    onSuccess: (data) => {
      setTokens(data.access_token, data.refresh_token)
      setCurrent('')
      setNext('')
      setConfirm('')
      push({ variant: 'success', title: t.passwordChanged, description: t.otherDevicesSignedOut, duration_ms: 4000 })
      void qc.invalidateQueries({ queryKey: ['auth-sessions'] })
    },
    onError: (err: Error) => {
      const msg = err.message === 'invalid_current_password' ? t.currentPasswordIncorrect : t.couldNotChangePassword
      push({ variant: 'error', title: t.changeFailed, description: msg, duration_ms: 4000 })
    },
  })

  const revoke = useMutation({
    mutationFn: async (id: string) => {
      const res = await fetch(apiUrl(`/auth/sessions/${id}/revoke`), { method: 'POST' })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
    },
    onSuccess: () => {
      push({ variant: 'info', title: t.sessionRevoked, description: t.deviceSignedOut, duration_ms: 3000 })
      void qc.invalidateQueries({ queryKey: ['auth-sessions'] })
    },
  })

  const canSubmit = current.length > 0 && next.length >= 6 && next === confirm && !changePassword.isPending

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-lg font-semibold text-[var(--color-text)]">{t.pageTitle}</h2>
        <p className="mt-1 text-sm text-[var(--color-text-muted)]">{t.pageDescription}</p>
      </header>

      {/* Change password */}
      <section className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)]">
        <header className="flex items-start gap-3 border-b border-[var(--color-border)] px-5 py-4">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[var(--color-surface-muted)] text-[var(--color-text-muted)]">
            <Key className="h-4 w-4" />
          </span>
          <div>
            <h3 className="text-sm font-semibold text-[var(--color-text)]">{t.changePasswordTitle}</h3>
            <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">{t.changePasswordHint}</p>
          </div>
        </header>
        <div className="space-y-3 px-5 py-4">
          <input type="password" autoComplete="current-password" placeholder={t.currentPassword} value={current}
            onChange={(e) => setCurrent(e.target.value)}
            className="w-full max-w-sm rounded-md border border-[var(--color-border-strong)] px-3 py-1.5 text-sm" />
          <input type="password" autoComplete="new-password" placeholder={t.newPassword} value={next}
            onChange={(e) => setNext(e.target.value)}
            className="w-full max-w-sm rounded-md border border-[var(--color-border-strong)] px-3 py-1.5 text-sm" />
          <input type="password" autoComplete="new-password" placeholder={t.confirmNewPassword} value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full max-w-sm rounded-md border border-[var(--color-border-strong)] px-3 py-1.5 text-sm" />
          {confirm.length > 0 && next !== confirm && (
            <p className="text-xs text-rose-600">{t.passwordsDoNotMatch}</p>
          )}
          <Button variant="primary" size="sm" disabled={!canSubmit} onClick={() => changePassword.mutate()}>
            {changePassword.isPending ? t.saving : t.updatePassword}
          </Button>
        </div>
      </section>

      {/* Active sessions */}
      <section className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)]">
        <header className="flex items-start gap-3 border-b border-[var(--color-border)] px-5 py-4">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[var(--color-surface-muted)] text-[var(--color-text-muted)]">
            <Monitor className="h-4 w-4" />
          </span>
          <div>
            <h3 className="text-sm font-semibold text-[var(--color-text)]">{t.sessionsTitle}</h3>
            <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">{t.sessionsDescription}</p>
          </div>
        </header>
        {sessions.isLoading ? (
          <div className="px-5 py-4 text-sm text-[var(--color-text-muted)]">{t.loading}</div>
        ) : sessions.isError ? (
          <div className="px-5 py-4 text-sm text-rose-700">{t.loadError}</div>
        ) : (sessions.data?.items.length ?? 0) === 0 ? (
          <div className="px-5 py-4 text-sm text-[var(--color-text-muted)]">{t.empty}</div>
        ) : (
          <ul className="divide-y divide-[var(--color-border)]">
            {sessions.data!.items.map((s) => (
              <li key={s.id} className="flex items-center justify-between px-5 py-3">
                <div>
                  <p className="text-sm font-medium text-[var(--color-text)]">{describeUserAgent(s.user_agent, t)}</p>
                  <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">
                    {s.ip_address ?? t.unknownIp} · {t.since(formatDateTime(s.issued_at, {}))}
                  </p>
                </div>
                <Button variant="ghost" size="sm" disabled={revoke.isPending} onClick={() => revoke.mutate(s.id)}>
                  {t.revoke}
                </Button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* 2FA + API tokens — honest V1.5 placeholders (no backend yet) */}
      <section className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] px-5 py-4">
        <div className="flex items-center gap-3">
          <ShieldCheck className="h-4 w-4 text-[var(--color-text-muted)]" />
          <p className="text-sm text-[var(--color-text-muted)]">{t.twoFactorAndTokens}</p>
          <Badge variant="neutral">{t.comingSoon}</Badge>
        </div>
      </section>
    </div>
  )
}

export function Security({ surface }: { surface: SettingsSurface }) {
  // Branch BEFORE any hook runs — the maket body lives in its own component so
  // hook order never depends on USE_REAL_API.
  if (USE_REAL_API) return <RealSecurity />
  return <MaketSecurity surface={surface} />
}

function MaketSecurity({ surface }: { surface: SettingsSurface }) {
  void surface
  const push = useToastStore((s) => s.push)
  const t = useT(securityI18n)

  const notifyMock = (label: string) =>
    push({
      variant: 'info',
      title: `${label} · mock-only`,
      description: t.toastTail,
      duration_ms: 4000,
    })

  return (
    <div className="space-y-6">
      <header>
        <h2 className="text-lg font-semibold text-[var(--color-text)]">{t.pageTitle}</h2>
        <p className="mt-1 text-sm text-[var(--color-text-muted)]">{t.pageDescription}</p>
      </header>

      {/* 2FA card */}
      <section className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)]">
        <header className="flex items-start justify-between gap-3 border-b border-[var(--color-border)] px-5 py-4">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[var(--color-surface-muted)] text-[var(--color-text-muted)]">
              <ShieldCheck className="h-4 w-4" />
            </span>
            <div>
              <h3 className="text-sm font-semibold text-[var(--color-text)]">
                {t.twoFactor.title}
              </h3>
              <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">
                {t.twoFactor.description}
              </p>
            </div>
          </div>
          <Badge variant="warning">{t.twoFactor.disabledBadge}</Badge>
        </header>
        <div className="px-5 py-4">
          <Button
            variant="primary"
            size="sm"
            onClick={() => notifyMock(t.twoFactor.enableLabel)}
          >
            {t.twoFactor.enableAction}
          </Button>
        </div>
      </section>

      {/* Active sessions */}
      <section className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)]">
        <header className="flex items-start justify-between gap-3 border-b border-[var(--color-border)] px-5 py-4">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[var(--color-surface-muted)] text-[var(--color-text-muted)]">
              <Monitor className="h-4 w-4" />
            </span>
            <div>
              <h3 className="text-sm font-semibold text-[var(--color-text)]">
                {t.sessions.title}
              </h3>
              <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">
                {t.sessions.description}
              </p>
            </div>
          </div>
        </header>
        <ul className="divide-y divide-[var(--color-border)]">
          {SESSIONS.map((s) => (
            <li
              key={s.id}
              className="flex items-center justify-between px-5 py-3"
            >
              <div>
                <p className="text-sm font-medium text-[var(--color-text)]">
                  {s.signature}
                </p>
                <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">
                  {s.isCurrent
                    ? `${t.sessions.currentSessionMeta} · ${t.sessions.lastActive(t.sessions.relativeJustNow)}`
                    : t.sessions.lastActive(t.sessions.relativeThreeHoursAgo)}
                </p>
              </div>
              {s.isCurrent ? (
                <Badge variant="success">{t.sessions.activeBadge}</Badge>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => notifyMock(t.sessions.revokeLabel)}
                >
                  {t.sessions.revokeAction}
                </Button>
              )}
            </li>
          ))}
        </ul>
      </section>

      {/* API tokens */}
      <section className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)]">
        <header className="flex items-start justify-between gap-3 border-b border-[var(--color-border)] px-5 py-4">
          <div className="flex items-start gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[var(--color-surface-muted)] text-[var(--color-text-muted)]">
              <Key className="h-4 w-4" />
            </span>
            <div>
              <h3 className="text-sm font-semibold text-[var(--color-text)]">
                {t.apiTokens.title}
              </h3>
              <p className="mt-0.5 text-xs text-[var(--color-text-muted)]">
                {t.apiTokens.description}
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => notifyMock(t.apiTokens.createLabel)}
          >
            {t.apiTokens.createAction}
          </Button>
        </header>
        <div className="px-5 py-4 text-sm text-[var(--color-text-muted)]">
          {t.apiTokens.emptyState}
        </div>
      </section>

      <p className="text-xs text-[var(--color-text-muted)]">{t.footerNote}</p>
    </div>
  )
}
